import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
/* Types */
import { PageProps } from '@components/IframeView/iframeview.types';
/* Utils */
import { iframeMessaging } from './iframeview.utils';
import { generateMessagePageActions } from './iframeview.baseactions';
import { sleep } from '@utils/helpers';
/* Components */
import LoaderPages from '@components/Loaders/Loader.pages';
/* Styles */
import styles from './iframeview.module.scss';

export const IFRAME_TAG = ({ src, id, title = 'iframepage-hellotech' }: { src: string; id: string; title?: string }) =>
  `<iframe style="visibility:hidden;" scrolling="no" onload="this.style.visibility='visible';" id="${id}" src="${src}" title="${title}" loading="eager"></iframe>`;

const IframeView = (props: PageProps) => {
  /* Local State */
  const [isLoading, setIsLoading] = useState(true);
  const [, setPageLoaded] = useState(false);
  const heightReceivedRef = useRef(false);
  const pageLoadedRef = useRef(false);
  const previousSrcRef = useRef<string | undefined>(undefined);

  /* Props */
  const { src, callerPageName, containerId, iframeId, additionalActions } = props;

  /* Constants */
  const containerIdName = containerId || `${callerPageName}-IframeContainer`;
  const iframeIdName = iframeId || `${callerPageName}-IframePage`;

  // @ts-ignore - generateMessagePageActions type signature mismatch (pre-existing issue)
  const messageAPI = useMemo(() => iframeMessaging(generateMessagePageActions(additionalActions)), [additionalActions]);

  const handlePageLoaded = useCallback(
    (loaded: boolean) => {
      setPageLoaded(loaded);
      if (loaded) {
        pageLoadedRef.current = true;

        const waitForVisibility = (checkCount = 0) => {
          const iframe = document.getElementById(iframeIdName) as HTMLIFrameElement;
          if (!iframe) {
            setIsLoading(false);
            return;
          }

          const isVisible = iframe.style.visibility !== 'hidden' && window.getComputedStyle(iframe).visibility !== 'hidden';
          if (isVisible) {
            setIsLoading(false);
            return;
          }

          const maxChecks = 100;
          if (checkCount >= maxChecks) {
            setIsLoading(false);
            return;
          }

          sleep(100).then(() => {
            waitForVisibility(checkCount + 1);
          });
        };

        waitForVisibility();
      }
    },
    [iframeIdName],
  );

  useEffect(() => {
    let cancelled = false;
    const partialMessageAPI = messageAPI({
      props,
      attributes: {
        CALLER_PAGE: callerPageName,
        // Type assertion: handlePageLoaded only accepts boolean, but type requires Dispatch<SetStateAction<boolean>>
        // This is safe because baseActions.loaded() always calls setPageLoaded(true)
        setPageLoaded: handlePageLoaded as React.Dispatch<React.SetStateAction<boolean>>,
        containerId: containerIdName,
        iframeId: iframeIdName,
      },
    });

    // Check if iframe is already loaded AND src hasn't changed before resetting state
    const iframe = document.getElementById(iframeIdName) as HTMLIFrameElement;
    const srcChanged = previousSrcRef.current !== undefined && previousSrcRef.current !== src;
    const isAlreadyLoaded = iframe?.contentWindow && pageLoadedRef.current && !srcChanged;

    // Track src for next render
    previousSrcRef.current = src;

    window.addEventListener('message', partialMessageAPI, false);

    // Only reset loading state if iframe hasn't loaded yet OR src changed
    if (!isAlreadyLoaded) {
      heightReceivedRef.current = false;
      pageLoadedRef.current = false;
      setIsLoading(true);
    }

    const timeoutFallback = async () => {
      await sleep(10000);
      if (!cancelled && !pageLoadedRef.current) {
        setIsLoading(false);
      }
    };
    timeoutFallback();

    return () => {
      cancelled = true;
      window.removeEventListener('message', partialMessageAPI, false);
    };
  }, [src, callerPageName, iframeIdName, messageAPI, props, handlePageLoaded, containerIdName]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.caller === callerPageName && event.data?.type === 'height') {
        heightReceivedRef.current = true;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [callerPageName]);

  useEffect(() => {
    let cancelled = false;

    const setFallbackHeight = async () => {
      await sleep(5000);

      if (!cancelled && !heightReceivedRef.current) {
        const container = document.getElementById(containerIdName);
        const iframe = document.getElementById(iframeIdName);
        if (container && iframe) {
          const defaultHeight = Math.max(window.innerHeight, 2000);
          container.style.height = `${defaultHeight}px`;
          iframe.style.height = `${defaultHeight}px`;
          iframe.style.width = '100%';
        }
      }
    };

    setFallbackHeight();

    return () => {
      cancelled = true;
    };
  }, [containerIdName, iframeIdName]);

  if (!src) return null;

  return (
    <section className={styles.iframeContainer}>
      {isLoading && (
        <div className={styles.loaderOverlay}>
          <LoaderPages message="" />
        </div>
      )}
      <div
        id={containerIdName}
        dangerouslySetInnerHTML={{
          __html: IFRAME_TAG({ src, id: iframeIdName, title: iframeIdName }),
        }}
      />
    </section>
  );
};

export default IframeView;

/*
  IframeView
  - This component is made to work with the original Webflow iframe implementation. Webflow allows us to attach some custom JS code, which we use
    to send postMessages from the iframe that we catch here in the app. In theory any 3rd party provider that allows custom JS code should
    work with this implementation

  How it works
  ------------
  - The iframe should have code attached that fires postMessage events that our page can capture and then act based on the tyep of message. E.g.,
    when the iframe loads it will fire a postMessage with its height, and our iframe container will execute a function to  adjust its height
    based on this postMessage

  Usage
  -----
  <IframeView callerPageName="foobarPage" src={https://path/to/content} />

  - At a bare minimum you'll need to provide a callerPageName and a src. The callerPageName is used to filter through
    postMessage actions and also to generate id properties for the HTML tags used in the component (see containerIdName and iframeIdName)
  - If you'd like to add/extend to the functions that respond to the iframe's postMessages see instructions in the generateMessagePageActions
    function in src/components/Elements/IframeView/iframeview.baseactions.ts
*/
